/**
 * REFACTORED: 2026-02-06
 * FROM src/components/dri-insights/DRIInsights.tsx
 * DRI Insights page – BU/individual DRI views, certifications, skill badges, training summary.
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChartAreaSkeleton } from '../components/common/loading';

import {
  Box,
  Alert,
Grid,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, getTableSyncStatus } from '../services/api';
import LastSyncedLabel from '../components/common/LastSyncedLabel';
import { useFilterOptions } from '../contexts/FilterOptionsContext';
import { FilterRequest } from '../types/api';
import { useAuth } from '../contexts/AuthContext';
import DriTopFilters, { type DriFilterUpdater } from '../components/filters/page/DriTopFilters';
import type {
  CertificationRecord,
  DRIValueType,
  MonthRange,
  SortDirection,
  IndividualDetailsSortColumn,
  EmployeeSummaryProductDetail,
  IndividualDetailsRow,
} from '../types/driInsights';
import { MONTH_NAMES, MONTH_ABBREVIATIONS } from '../constants/driInsightsConstants';
import CertificationsTrendChart from '../components/dri-insights/CertificationsTrendChart';
import {
  calculateTier,
  getDefaultCertificationMonths,
  computeTotalActiveEmployees,
  computeRawData,
  computeAllProducts,
  computeOptionalProducts,
  computeAvailableProducts,
  computeFilteredData,
  computeCertificationsData,
  computeSkillBadgesData,
  computeEmployeeTrainingsSummaryData,
  computeEmployeeSummaryData,
  computeIndividualDetailsRows,
  computeCertificationMetrics,
  computeSummaryMetrics,
  driValueForProductDetail,
} from '../utils/driInsightsUtils';
import { formatDateEnhanced } from '../utils/DateUtils';
import {
  createFilterClickHandler,
  createFilterCloseHandler,
  createNumericFilterChangeHandler,
  createApplyNumericFilterHandler,
  createClearFilterHandler,
  createClearAllFiltersHandler,
  createCheckboxFilterToggleHandler,
  createApplyCheckboxFilterHandler,
  createSelectAllCheckboxFilterHandler,
  createClearAllCheckboxFilterHandler,
  hasActiveFilter as hasActiveFilterFromUtils,
  hasFiltersChanged,
} from '../utils/FilterUtils';
import DRISummaryCards from '../components/dri-insights/DRISummaryCards';
import DRICharts from '../components/dri-insights/DRICharts';
import CertificationsInsightsSection from '../components/dri-insights/CertificationsInsightsSection';
import Tier1DRIProfilesTrendChart from '../components/dri-insights/Tier1DRIProfilesTrendChart';
import NewCompetenciesTrendChart from '../components/dri-insights/NewCompetenciesTrendChart';

const DRIInsightsPage: React.FC = () => {
  const { user } = useAuth();
  
  const [showDriProfilesWithout, setShowDriProfilesWithout] = useState(false);
  const { filterOptions, isLoading: filterOptionsLoading } = useFilterOptions();
  const [filters, setFilters] = useState<FilterRequest>({
    scope: 'delivery',
    business_units: [],
    sub_bus: [],
    products: [],
  });
  const [activeFilters, setActiveFilters] = useState<FilterRequest>({
    scope: 'delivery',
    business_units: [],
    sub_bus: [],
    products: [],
  });

  const { data: syncData, isLoading: syncLoading } = useQuery({
    queryKey: ['sync-status'],
    queryFn: getTableSyncStatus,
    staleTime: 10 * 60 * 1000,
    enabled: !!user,
  });

  const driQueriesEnabled =
    activeFilters.scope === 'org' ||
    !!(activeFilters.business_units && activeFilters.business_units.length > 0) ||
    !!(activeFilters.sub_bus && activeFilters.sub_bus.length > 0);

  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: {
      text?: string;
      min?: number | null;
      max?: number | null;
      selectedValues?: string[];
      startDate?: Date | null;
      endDate?: Date | null;
    };
  }>({});

  const [tempSelectedValues, setTempSelectedValues] = useState<{
    [key: string]: string[];
  }>({});

  const [tempNumericFilters, setTempNumericFilters] = useState<{
    [key: string]: {
      min?: number | null;
      max?: number | null;
    };
  }>({});

  const [filterSearchText, setFilterSearchText] = useState<{
    [key: string]: string;
  }>({});

  const [filterAnchorEl, setFilterAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});

  const [gridCellValueType, setGridCellValueType] = useState<DRIValueType>('total_dri');

  const defaultCertificationMonths = getDefaultCertificationMonths();

  const [certTrendStartMonth, setCertTrendStartMonth] = useState<MonthRange>(defaultCertificationMonths.startMonth);
  const [certTrendEndMonth, setCertTrendEndMonth] = useState<MonthRange>(defaultCertificationMonths.endMonth);
  const [certTrendChartType, setCertTrendChartType] = useState<'new' | 'active' | 'expiring'>('new');

  useEffect(() => {
    if (filters.scope === 'org') return;
    if (filterOptions && filterOptions.business_units && filterOptions.business_units.length > 0) {
      setFilters((prev) => ({
        ...prev,
        scope: 'delivery',
        business_units: filterOptions.business_units,
        sub_bus: filterOptions.sub_bus || [],
      }));
      setActiveFilters((prev) => ({
        ...prev,
        scope: 'delivery',
        business_units: filterOptions.business_units,
        sub_bus: filterOptions.sub_bus || [],
      }));
    }
  }, [filterOptions?.business_units, filterOptions?.sub_bus, filters.scope]);

  const { data: driInsights, isLoading, error, refetch } = useQuery({
    queryKey: ['dri-insights', activeFilters],
    queryFn: () => dashboardApi.getDriInsights(activeFilters),
    enabled: driQueriesEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const handleFiltersChange = (next: DriFilterUpdater) => {
    if (typeof next === 'function') {
      setFilters(next);
    } else {
      setFilters(next);
    }
  };

  const handleRefresh = () => {
    const hasFilterChanges = JSON.stringify(filters) !== JSON.stringify(activeFilters);
    setActiveFilters(filters);
    if (!hasFilterChanges) {
      refetch();
    }
  };

  const filtersChanged = hasFiltersChanged(filters, activeFilters);

  const rawData = useMemo(() => computeRawData(driInsights), [driInsights]);
  const totalActiveEmployees = useMemo(() => computeTotalActiveEmployees(driInsights), [driInsights]);

  const {
    data: reactiveProductOptions,
    isLoading: productsLoading
  } = useQuery({
    queryKey: [
      'product-options',
      user?.id,
      filters.scope,
      filters.scope === 'org' ? 'all' : filters.business_units,
    ],
    queryFn: async () => {
      const scope = filters.scope || 'delivery';
      if (scope === 'delivery' && (!filters.business_units || filters.business_units.length === 0)) {
        return [];
      }
      return await dashboardApi.getProductsByBusinessUnits(
        filters.business_units || [],
        scope
      );
    },
    enabled:
      !!user &&
      (filters.scope === 'org' ||
        !!(filters.business_units && filters.business_units.length > 0)),
    staleTime: 5 * 60 * 1000,
  });

  const isOrgScope = filters.scope === 'org';

  const allProducts = useMemo(
    () =>
      computeAllProducts(rawData, filterOptions?.products, {
        skipFilterOptionsProducts: isOrgScope,
      }),
    [rawData, filterOptions?.products, isOrgScope]
  );

  /** Org: all BQ DRI products (no bu_to_product). Delivery: BU-mapped products from Postgres. */
  const assignedProducts = useMemo(() => {
    if (isOrgScope && reactiveProductOptions && reactiveProductOptions.length > 0) {
      return reactiveProductOptions;
    }
    if (filters.business_units && filters.business_units.length > 0 && reactiveProductOptions && reactiveProductOptions.length > 0) {
      return reactiveProductOptions;
    }
    return [];
  }, [isOrgScope, filters.business_units, reactiveProductOptions]);

  const optionalProducts = useMemo(() => {
    if (isOrgScope) return [];
    return computeOptionalProducts(allProducts, assignedProducts);
  }, [isOrgScope, allProducts, assignedProducts]);

  const availableProducts = useMemo(
    () => computeAvailableProducts(assignedProducts, optionalProducts),
    [assignedProducts, optionalProducts]
  );

  /** Org scope: default-select all products when the org product list loads. */
  useEffect(() => {
    if (!isOrgScope || !reactiveProductOptions?.length) return;
    setFilters((prev) => {
      const next = [...reactiveProductOptions];
      const cur = prev.products || [];
      if (cur.length === next.length && next.every((p) => cur.includes(p))) {
        return prev;
      }
      return { ...prev, products: next };
    });
  }, [isOrgScope, reactiveProductOptions]);

  /** Delivery scope: sync products when BU-mapped assigned list changes. */
  const driBuSelectionKeyRef = useRef<string>('');
  useEffect(() => {
    if (isOrgScope) return;

    const bus = filters.business_units || [];
    const buKey = [...bus].sort().join('\0');
    if (assignedProducts.length === 0) {
      driBuSelectionKeyRef.current = buKey;
      return;
    }
    if (bus.length === 0) {
      driBuSelectionKeyRef.current = buKey;
      return;
    }

    const buSelectionChanged = driBuSelectionKeyRef.current !== buKey;
    driBuSelectionKeyRef.current = buKey;

    setFilters((prev: FilterRequest) => {
      const assignedSet = new Set(assignedProducts);
      if (buSelectionChanged) {
        return { ...prev, products: [...assignedProducts] };
      }
      const prevProducts = prev.products || [];
      const userExtras = prevProducts.filter((p) => !assignedSet.has(p));
      return { ...prev, products: [...assignedProducts, ...userExtras] };
    });
  }, [isOrgScope, assignedProducts, filters.business_units]);

  const filteredData = useMemo(() => computeFilteredData(rawData, activeFilters.products), [rawData, activeFilters.products]);

  const { data: certificationsAndSkillBadgesData, isLoading: isLoadingCertsAndSkills } = useQuery({
    queryKey: ['dri-insights-certifications-skill-badges', activeFilters],
    queryFn: () => dashboardApi.getDriInsightsCertificationsAndSkillBadges(activeFilters),
    enabled: driQueriesEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const { data: unregisteredEmployeesData, isLoading: isLoadingUnregistered } = useQuery({
    queryKey: ['dri-insights-unregistered-employees', activeFilters],
    queryFn: () => dashboardApi.getUnregisteredEmployees(activeFilters),
    enabled: driQueriesEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const { data: tier1TrendData = [] } = useQuery({
    queryKey: ['dri-insights-tier1-trend', activeFilters],
    queryFn: () => dashboardApi.getDriInsightsTier1Trend(activeFilters),
    enabled: driQueriesEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const { data: competencyTrendData = [] } = useQuery({
    queryKey: ['dri-insights-competency-trend', activeFilters],
    queryFn: () => dashboardApi.getDriInsightsCompetencyTrend(activeFilters),
    enabled: driQueriesEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const certificationsData = useMemo(() => computeCertificationsData(certificationsAndSkillBadgesData, filteredData), [certificationsAndSkillBadgesData, filteredData]);

  const monthlyCertificationsTrendPage = useMemo(() => {
    if (!certificationsData || certificationsData.length === 0) return [];
    const startMonth = certTrendStartMonth || defaultCertificationMonths.startMonth;
    const endMonth = certTrendEndMonth || defaultCertificationMonths.endMonth;
    if (!startMonth || !endMonth) return [];
    const certs = certificationsData;
    const targetStartYear = startMonth.year;
    const targetStartMonth = startMonth.month;
    const targetEndYear = endMonth.year;
    const targetEndMonth = endMonth.month;
    const months: { month: string; year: number; monthAbbr: string; sortKey: string }[] = [];
    let currentYear = targetStartYear;
    let currentMonth = targetStartMonth;
    while (
      currentYear < targetEndYear ||
      (currentYear === targetEndYear && currentMonth <= targetEndMonth)
    ) {
      months.push({
        month: MONTH_NAMES[currentMonth - 1],
        year: currentYear,
        monthAbbr: `${MONTH_ABBREVIATIONS[currentMonth - 1]} ${currentYear}`,
        sortKey: `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
      });
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    return months.map((monthData) => {
      const targetMonthIndex = MONTH_NAMES.indexOf(monthData.month) + 1;
      const count = certs.filter((cert: CertificationRecord) => {
        if (!cert.issue_date) return false;
        const issueDate = new Date(cert.issue_date);
        const issueYear = issueDate.getFullYear();
        const issueMonth = issueDate.getMonth() + 1;
        return issueYear === monthData.year && issueMonth === targetMonthIndex;
      }).length;
      return { ...monthData, count };
    });
  }, [certificationsData, certTrendStartMonth, certTrendEndMonth, defaultCertificationMonths.startMonth, defaultCertificationMonths.endMonth]);

  const activeCertificationsTrendPage = useMemo(() => {
    if (!certificationsData || certificationsData.length === 0) return [];
    const startMonth = certTrendStartMonth || defaultCertificationMonths.startMonth;
    const endMonth = certTrendEndMonth || defaultCertificationMonths.endMonth;
    if (!startMonth || !endMonth) return [];
    const certs = certificationsData;
    const targetStartYear = startMonth.year;
    const targetStartMonth = startMonth.month;
    const targetEndYear = endMonth.year;
    const targetEndMonth = endMonth.month;
    const months: { month: string; year: number; monthAbbr: string; sortKey: string }[] = [];
    let currentYear = targetStartYear;
    let currentMonth = targetStartMonth;
    while (
      currentYear < targetEndYear ||
      (currentYear === targetEndYear && currentMonth <= targetEndMonth)
    ) {
      months.push({
        month: MONTH_NAMES[currentMonth - 1],
        year: currentYear,
        monthAbbr: `${MONTH_ABBREVIATIONS[currentMonth - 1]} ${currentYear}`,
        sortKey: `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
      });
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    return months.map((monthData) => {
      const targetMonthIndex = MONTH_NAMES.indexOf(monthData.month) + 1;
      const monthDate = new Date(monthData.year, targetMonthIndex - 1, 1);
      const nextMonthDate = new Date(monthData.year, targetMonthIndex, 1);
      const count = certs.filter((cert: CertificationRecord) => {
        if (!cert.issue_date) return false;
        const issueDate = new Date(cert.issue_date);
        const expiryDate = cert.expiry_date ? new Date(cert.expiry_date) : null;
        const wasIssuedByEnd = issueDate < nextMonthDate;
        const hasNotExpiredByStart = !expiryDate || expiryDate >= monthDate;
        return wasIssuedByEnd && hasNotExpiredByStart;
      }).length;
      return { ...monthData, count };
    });
  }, [certificationsData, certTrendStartMonth, certTrendEndMonth, defaultCertificationMonths.startMonth, defaultCertificationMonths.endMonth]);

  const expiringCertificationsTrendPage = useMemo(() => {
    if (!certificationsData || certificationsData.length === 0) return [];
    const startMonth = certTrendStartMonth || defaultCertificationMonths.startMonth;
    const endMonth = certTrendEndMonth || defaultCertificationMonths.endMonth;
    if (!startMonth || !endMonth) return [];
    const certs = certificationsData;
    const targetStartYear = startMonth.year;
    const targetStartMonth = startMonth.month;
    const targetEndYear = endMonth.year;
    const targetEndMonth = endMonth.month;
    const months: { month: string; year: number; monthAbbr: string; sortKey: string }[] = [];
    let currentYear = targetStartYear;
    let currentMonth = targetStartMonth;
    while (
      currentYear < targetEndYear ||
      (currentYear === targetEndYear && currentMonth <= targetEndMonth)
    ) {
      months.push({
        month: MONTH_NAMES[currentMonth - 1],
        year: currentYear,
        monthAbbr: `${MONTH_ABBREVIATIONS[currentMonth - 1]} ${currentYear}`,
        sortKey: `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
      });
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    return months.map((monthData) => {
      const targetMonthIndex = MONTH_NAMES.indexOf(monthData.month) + 1;
      const count = certs.filter((cert: CertificationRecord) => {
        if (!cert.expiry_date) return false;
        const expiryDate = new Date(cert.expiry_date);
        const expiryYear = expiryDate.getFullYear();
        const expiryMonth = expiryDate.getMonth() + 1;
        return expiryYear === monthData.year && expiryMonth === targetMonthIndex;
      }).length;
      return { ...monthData, count };
    });
  }, [certificationsData, certTrendStartMonth, certTrendEndMonth, defaultCertificationMonths.startMonth, defaultCertificationMonths.endMonth]);

  const skillBadgesData = useMemo(() => computeSkillBadgesData(certificationsAndSkillBadgesData, filteredData), [certificationsAndSkillBadgesData, filteredData]);
  const employeeTrainingsSummaryData = useMemo(() => computeEmployeeTrainingsSummaryData(filteredData, certificationsAndSkillBadgesData), [filteredData, certificationsAndSkillBadgesData]);

  const employeeSummaryData = useMemo(() => computeEmployeeSummaryData(filteredData), [filteredData]);
  const individualDetailsRows = useMemo(() => computeIndividualDetailsRows(employeeSummaryData), [employeeSummaryData]);

  const filteredIndividualDetails = useMemo(() => {
    return individualDetailsRows.filter((row) => {
      if (columnFilters.individual_employee_name?.selectedValues?.length && !columnFilters.individual_employee_name.selectedValues.includes(row.employee.member_name)) return false;
      if (columnFilters.individual_email?.selectedValues?.length && !columnFilters.individual_email.selectedValues.includes(row.employee.member_email)) return false;
      if (columnFilters.individual_product?.selectedValues?.length && !columnFilters.individual_product.selectedValues.includes(row.product.product_name)) return false;
      if (columnFilters.individual_bu?.selectedValues?.length && !columnFilters.individual_bu.selectedValues.includes(row.employee.business_unit)) return false;
      if (columnFilters.individual_sub_bu?.selectedValues?.length && !columnFilters.individual_sub_bu.selectedValues.includes(row.employee.sub_business_unit || '')) return false;

      const numCols = {
        individual_total_dri: row.product.total_dri,
        individual_certification: row.product.dri_through_technical_certification,
        individual_labs: row.product.dri_through_labs,
        individual_projects: row.product.dri_through_project_deployed,
        individual_skill_badges: row.product.dri_through_skill_badges,
        individual_other_cloud: row.product.dri_through_other_cloud_experience,
        individual_training: row.product.dri_through_technical_training,
      };

      for (const [colId, val] of Object.entries(numCols)) {
        if (columnFilters[colId]) {
          const { min, max } = columnFilters[colId];
          if (min !== null && min !== undefined && val < min) return false;
          if (max !== null && max !== undefined && val > max) return false;
        }
      }

      if (columnFilters.individual_tier?.selectedValues?.length) {
        const tier = `Tier ${calculateTier(row.product.total_dri)}`;
        if (!columnFilters.individual_tier.selectedValues.includes(tier)) return false;
      }

      return true;
    });
  }, [individualDetailsRows, columnFilters]);

  const gridColumns = useMemo(() => {
    const productSet = new Set<string>();
    individualDetailsRows.forEach((row) => {
      if (row.product.product_name && row.product.product_name !== '-') productSet.add(row.product.product_name);
    });
    let columns = Array.from(productSet).sort();
    if (columnFilters.individual_product?.selectedValues?.length) {
      columns = columns.filter((p) => columnFilters.individual_product.selectedValues!.includes(p));
    }
    return columns;
  }, [individualDetailsRows, columnFilters.individual_product]);

  const gridRows = useMemo(() => {
    const employeeMap = new Map<string, IndividualDetailsRow>();
    filteredIndividualDetails.forEach((row) => {
      const email = row.employee.member_email;
      if (!employeeMap.has(email)) employeeMap.set(email, { employee: row.employee, products: {} });
      if (row.product.product_name !== '-') employeeMap.get(email)!.products[row.product.product_name] = row.product;
    });
    let rows = Array.from(employeeMap.values());
    const productFilters = Object.entries(columnFilters).filter(([key]) => key.startsWith('product_'));
    if (productFilters.length > 0) {
      rows = rows.filter((row) => {
        for (const [filterColId, filter] of productFilters) {
          const productName = filterColId.replace('product_', '');
          const val = driValueForProductDetail(row.products[productName], gridCellValueType);
          const { min, max } = filter;
          if (min !== null && min !== undefined && val < min) return false;
          if (max !== null && max !== undefined && val > max) return false;
        }
        return true;
      });
    }
    return rows;
  }, [filteredIndividualDetails, columnFilters, gridCellValueType]);

  const certificationMetrics = useMemo(() => computeCertificationMetrics(certificationsData, employeeSummaryData), [certificationsData, employeeSummaryData]);
  const summaryMetrics = useMemo(() => computeSummaryMetrics(employeeSummaryData, totalActiveEmployees), [employeeSummaryData, totalActiveEmployees]);

  const { data: weeklyTrendsResponse, isLoading: isLoadingWeeklyTrends } = useQuery({
    queryKey: ['dri-insights-weekly-trends', activeFilters],
    queryFn: () => dashboardApi.getDriWeeklyTrends(activeFilters),
    enabled: driQueriesEnabled && !productsLoading,
    staleTime: 5 * 60 * 1000,
  });

  let weeklyTrendsRaw: any[] = [];
  let availableProductsFromApi: string[] = [];
  if (weeklyTrendsResponse) {
    if (typeof weeklyTrendsResponse === 'object' && 'weekly_trends' in weeklyTrendsResponse) {
      weeklyTrendsRaw = (weeklyTrendsResponse as any).weekly_trends || [];
      const fromApi = (weeklyTrendsResponse as any).available_products || [];
      availableProductsFromApi =
        fromApi.length > 0
          ? fromApi
          : activeFilters.scope === 'org' && activeFilters.products?.length
            ? [...activeFilters.products]
            : fromApi;
    } else if (Array.isArray(weeklyTrendsResponse)) {
      weeklyTrendsRaw = weeklyTrendsResponse;
      availableProductsFromApi = Array.from(new Set(weeklyTrendsRaw.map(r => r.product_name).filter(Boolean)));
    }
  }

  const unregisteredEmployeesTableRef = useRef<HTMLDivElement>(null);

  const handleFilterCloseImpl = createFilterCloseHandler(setFilterAnchorEl, filterAnchorEl);
  const handleFilterClick = createFilterClickHandler(setFilterAnchorEl, filterAnchorEl, {
    tempSelectedValues,
    setTempSelectedValues,
    columnFilters,
    tempNumericFilters,
    setTempNumericFilters,
  });
  const handleNumericFilterChange = createNumericFilterChangeHandler(setTempNumericFilters, tempNumericFilters);
  const handleApplyNumericFilter = createApplyNumericFilterHandler(setColumnFilters, columnFilters, tempNumericFilters, handleFilterCloseImpl);
  const handleClearFilter = createClearFilterHandler(setColumnFilters, columnFilters, handleFilterCloseImpl);
  const handleClearAllFilters = createClearAllFiltersHandler(
    setColumnFilters,
    setFilterAnchorEl,
    setTempSelectedValues,
    setTempNumericFilters,
    setFilterSearchText
  );

  const getNumericRange = useCallback(
    (columnId: string) => {
      if (!columnId.startsWith('product_')) return { min: 0, max: 100 };
      const productName = columnId.replace('product_', '');
      let min = Infinity;
      let max = -Infinity;
      gridRows.forEach((row) => {
        const v = driValueForProductDetail(row.products[productName], gridCellValueType);
        if (v < min) min = v;
        if (v > max) max = v;
      });
      if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 100 };
      if (min === max) return { min: Math.max(0, min - 1), max: max + 1 };
      return { min, max };
    },
    [gridRows, gridCellValueType]
  );

  const getAvailableValuesForColumn = (columnId: string): string[] => {
    const valueSet = new Set<string>();
    individualDetailsRows.forEach((row) => {
      if (columnId === 'individual_employee_name') valueSet.add(row.employee.member_name);
      else if (columnId === 'individual_email') valueSet.add(row.employee.member_email);
      else if (columnId === 'individual_product') valueSet.add(row.product.product_name);
      else if (columnId === 'individual_bu') valueSet.add(row.employee.business_unit);
      else if (columnId === 'individual_sub_bu') valueSet.add(row.employee.sub_business_unit || '');
      else if (columnId === 'individual_tier') valueSet.add(`Tier ${calculateTier(row.product.total_dri)}`);
    });
    return Array.from(valueSet).filter(Boolean).sort();
  };

  const hasActiveFilter = (columnId: string) => hasActiveFilterFromUtils(columnFilters, columnId);

  return (
    <Box sx={{ pr: 3, pl: 3 }}>
      <Box sx={{ mb: 2 ,mt: 2 }}>
        <DriTopFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onRefresh={handleRefresh}
          hasUnsavedChanges={filtersChanged}
          assignedProducts={assignedProducts}
          optionalProducts={optionalProducts}
          productsLoading={productsLoading}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <LastSyncedLabel isoTimestamp={syncData?.dri_insights} isLoading={syncLoading} />
      </Box>
      
      {!isLoading && !error && filteredData.length > 0 && (
        <DRISummaryCards
          summaryMetrics={summaryMetrics}
          certificationMetrics={certificationMetrics}
          showDriProfilesWithout={showDriProfilesWithout}
          onToggleDriProfilesWithout={() => setShowDriProfilesWithout(!showDriProfilesWithout)}
          onUnregisteredClick={() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                unregisteredEmployeesTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              });
            });
          }}
        />
      )}

      {isLoading && (
        <Box sx={{ px: 2, py: 2 }}>
          <ChartAreaSkeleton titleWidth="45%" height={360} />
        </Box>
      )}

      {!isLoading && !error && (
        <Box>
          {filteredData.length === 0 ? (
            <Alert severity="info">Please select a Business Unit or Sub-Business Unit.</Alert>
          ) : (
            <Box>
              <DRICharts filteredData={filteredData} employeeSummaryData={employeeSummaryData} weeklyTrendsRaw={weeklyTrendsRaw} availableProductsFromApi={availableProductsFromApi} isLoadingWeeklyTrends={isLoadingWeeklyTrends} />
              <Box sx={{ mb: 3, mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={4}>
                    <Box sx={{ height: 500 }}>
                      <Tier1DRIProfilesTrendChart filters={activeFilters} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <Box sx={{ height: 500 }}>
                      <NewCompetenciesTrendChart filters={activeFilters} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <Box sx={{ height: 500 }}>
                      <CertificationsTrendChart
                        data={monthlyCertificationsTrendPage}
                        startMonth={certTrendStartMonth}
                        endMonth={certTrendEndMonth}
                        onMonthRangeChange={(start: MonthRange, end: MonthRange) => {
                          setCertTrendStartMonth(start);
                          setCertTrendEndMonth(end);
                        }}
                        title=""
                        lineName="New Certifications"
                        chartType={certTrendChartType}
                        onChartTypeChange={setCertTrendChartType}
                        newCertificationsData={monthlyCertificationsTrendPage}
                        activeCertificationsData={activeCertificationsTrendPage}
                        expiringCertificationsData={expiringCertificationsTrendPage}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <CertificationsInsightsSection
                certificationsData={certificationsData}
                defaultCertificationMonths={defaultCertificationMonths}
                activeFilters={activeFilters}
                formatDate={(d) => formatDateEnhanced(d ?? null)}
                gridCellValueType={gridCellValueType}
                onGridCellValueTypeChange={setGridCellValueType}
                onClearAllFilters={handleClearAllFilters}
                individualDetailsActiveFilterCount={Object.keys(columnFilters).filter((key) => key.startsWith('individual_')).length}
                individualDetailsRows={gridRows}
                gridColumns={gridColumns}
                individualDetailsPaginationResetDeps={columnFilters}
                onFilterClick={handleFilterClick}
                individualDetailsFilterState={{
                  filterAnchorEl,
                  columnFilters,
                  tempSelectedValues,
                  filterSearchText,
                  tempNumericFilters,
                }}
                individualDetailsFilterHandlers={{
                  getAvailableValuesForColumn,
                  hasActiveFilter,
                  onFilterClose: handleFilterCloseImpl,
                  onApplyCheckboxFilter: (id) =>
                    createApplyCheckboxFilterHandler(
                      setColumnFilters,
                      columnFilters,
                      setTempSelectedValues,
                      tempSelectedValues,
                      setFilterSearchText,
                      filterSearchText,
                      handleFilterCloseImpl
                    )(id),
                  onSelectAllCheckboxFilter: (id, vals) =>
                    createSelectAllCheckboxFilterHandler(setTempSelectedValues, tempSelectedValues, id)(vals),
                  onClearAllCheckboxFilter: (id) =>
                    createClearAllCheckboxFilterHandler(setTempSelectedValues, tempSelectedValues, id)(),
                  onCheckboxFilterToggle: (id, val) =>
                    createCheckboxFilterToggleHandler(setTempSelectedValues, tempSelectedValues, columnFilters, id)(val),
                  onFilterSearchTextChange: (id, val) => setFilterSearchText({ ...filterSearchText, [id]: val }),
                  onClearFilterForColumn: (id) => handleClearFilter(id),
                  getNumericRange,
                  onApplyNumericFilter: handleApplyNumericFilter,
                  onNumericFilterChange: handleNumericFilterChange,
                }}
                unregisteredTableRef={unregisteredEmployeesTableRef}
                isLoadingUnregistered={isLoadingUnregistered}
                unregisteredEmployeesData={unregisteredEmployeesData ?? []}
                employeeTrainingsSummaryData={employeeTrainingsSummaryData}
                isLoadingCertsAndSkills={isLoadingCertsAndSkills}
                skillBadgesData={skillBadgesData}
                tier1TrendData={tier1TrendData}
                competencyTrendData={competencyTrendData}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DRIInsightsPage;
